import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import { resetPassword } from '../api/api';
import { useLocation, useNavigate } from 'react-router-dom';

const validationSchema = Yup.object({
    password: Yup.string().min(6, 'Password must be at least 6 characters').max(50, 'Password must be at most 50 characters').required('Password is required'),
    confirmPassword: Yup.string()
        .required('Confirm Password is required')
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    // 🔹 Extract token and email from URL
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const email = params.get('email');

    const formik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                // Add API call here
                console.log('Form submitted:', values);
                
                setLoading(true);
                const res = await resetPassword({
                    token,
                    email,
                    newPassword: values.password,
                });
                toast.success(res.message);
                
                // Reset form
                formik.resetForm();
                setLoading(false);

                // Redirect to profile or homepage
                setTimeout(() => {
                    navigate('/member-login');
                }, 3000);
            } catch (error) {
                setLoading(false);
            }

        },
    });

    return (
        <div className="min-h-screen flex md:flex-row">
            {/* Left: Form */}
            <div className="md:w-1/2 w-full bg-[#212121] text-white py-[25px] px-[25px] sm:px-[50px] lg:px-[91px] lg:py[66px]  flex flex-col justify-center block">
                <div className="mb-6 text-center">
                    <img src='./logo-transparent-png.png' alt="Logo" className="mx-auto w-[70%]" />
                </div>

                <form onSubmit={formik.handleSubmit} className="space-y-4">

                    {/* Password */}
                    <div>
                        <label className="text-sm block mb-1 text-left">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.password}
                                className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white pr-10"
                                placeholder="Password"
                            />
                            <span
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-2.5 cursor-pointer text-gray-400 hover:text-white"
                            >
                                {showPassword ? <svg viewBox="64 64 896 896" focusable="false" data-icon="eye-invisible" width="1em" height="1em" fill="white" aria-hidden="true"><path d="M942.2 486.2Q889.47 375.11 816.7 305l-50.88 50.88C807.31 395.53 843.45 447.4 874.7 512 791.5 684.2 673.4 766 512 766q-72.67 0-133.87-22.38L323 798.75Q408 838 512 838q288.3 0 430.2-300.3a60.29 60.29 0 000-51.5zm-63.57-320.64L836 122.88a8 8 0 00-11.32 0L715.31 232.2Q624.86 186 512 186q-288.3 0-430.2 300.3a60.3 60.3 0 000 51.5q56.69 119.4 136.5 191.41L112.48 835a8 8 0 000 11.31L155.17 889a8 8 0 0011.31 0l712.15-712.12a8 8 0 000-11.32zM149.3 512C232.6 339.8 350.7 258 512 258c54.54 0 104.13 9.36 149.12 28.39l-70.3 70.3a176 176 0 00-238.13 238.13l-83.42 83.42C223.1 637.49 183.3 582.28 149.3 512zm246.7 0a112.11 112.11 0 01146.2-106.69L401.31 546.2A112 112 0 01396 512z"></path><path d="M508 624c-3.46 0-6.87-.16-10.25-.47l-52.82 52.82a176.09 176.09 0 00227.42-227.42l-52.82 52.82c.31 3.38.47 6.79.47 10.25a111.94 111.94 0 01-112 112z"></path></svg> : <svg viewBox="64 64 896 896" focusable="false" data-icon="eye" width="1em" height="1em" fill="white" aria-hidden="true"><path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path></svg>}
                            </span>
                        </div>
                        {formik.touched.password && formik.errors.password && (
                            <p className="text-red-400 text-sm mt-1 text-left">{formik.errors.password}</p>
                        )}
                    </div>
                    {/* Confirm Password */}
                    <div>
                        <label className="text-sm block mb-1 text-left">Confirm Password</label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.confirmPassword}
                                className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white pr-10"
                                placeholder="Password"
                            />
                            <span
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-2.5 cursor-pointer text-gray-400 hover:text-white"
                            >
                                {showConfirmPassword ? <svg viewBox="64 64 896 896" focusable="false" data-icon="eye-invisible" width="1em" height="1em" fill="white" aria-hidden="true"><path d="M942.2 486.2Q889.47 375.11 816.7 305l-50.88 50.88C807.31 395.53 843.45 447.4 874.7 512 791.5 684.2 673.4 766 512 766q-72.67 0-133.87-22.38L323 798.75Q408 838 512 838q288.3 0 430.2-300.3a60.29 60.29 0 000-51.5zm-63.57-320.64L836 122.88a8 8 0 00-11.32 0L715.31 232.2Q624.86 186 512 186q-288.3 0-430.2 300.3a60.3 60.3 0 000 51.5q56.69 119.4 136.5 191.41L112.48 835a8 8 0 000 11.31L155.17 889a8 8 0 0011.31 0l712.15-712.12a8 8 0 000-11.32zM149.3 512C232.6 339.8 350.7 258 512 258c54.54 0 104.13 9.36 149.12 28.39l-70.3 70.3a176 176 0 00-238.13 238.13l-83.42 83.42C223.1 637.49 183.3 582.28 149.3 512zm246.7 0a112.11 112.11 0 01146.2-106.69L401.31 546.2A112 112 0 01396 512z"></path><path d="M508 624c-3.46 0-6.87-.16-10.25-.47l-52.82 52.82a176.09 176.09 0 00227.42-227.42l-52.82 52.82c.31 3.38.47 6.79.47 10.25a111.94 111.94 0 01-112 112z"></path></svg> : <svg viewBox="64 64 896 896" focusable="false" data-icon="eye" width="1em" height="1em" fill="white" aria-hidden="true"><path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path></svg>}
                            </span>
                        </div>
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                            <p className="text-red-400 text-sm mt-1 text-left">{formik.errors.confirmPassword}</p>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-teal-500 hover:bg-teal-600 text-white py-2 rounded mt-4"
                        disabled={loading}
                    >
                        Update
                    </button>
                    {/* Footer */}
                    <div className="mt-6 text-sm text-center text-gray-400 space-y-1 flex justify-end items-baseline">
                        {/* <p>613-720-5021</p>
                        <p>|</p>
                        <p>Have a question?</p>
                        <p>|</p> */}
                        <p><span className="text-teal-400 underline cursor-pointer hover:text-teal-500" onClick={() => navigate('/member-login')}>
                            Member Login
                        </span></p>
                    </div>
                </form>

            </div>

            {/* Right: Image */}
            <div className="md:w-1/2 w-full bg-gray-100 flex justify-center hidden md:block">
                <img
                    src="/parking-image.png"
                    alt="Parking"
                    className="w-full h-full object-cover"
                />
            </div>
            <ToastContainer
                closeButton={false}
                autoClose={5000}
                position='top-right'
            />
        </div>
    );
};

export default ResetPassword;
